import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, Loader2, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { logger } from '../utils/logger';

interface CDNTestResult {
  url: string;
  status: 'loading' | 'success' | 'error';
  loadTime?: number;
  fromCache?: boolean;
  headers?: Record<string, string>;
}

export function CDNDebugPanel() {
  const [isVisible, setIsVisible] = useState(false);
  const [tests, setTests] = useState<CDNTestResult[]>([]);
  const [isTestingCDN, setIsTestingCDN] = useState(false);

  // Show panel via console command
  useEffect(() => {
    (window as any).__showCDNDebug = () => setIsVisible(true);
    (window as any).__hideCDNDebug = () => setIsVisible(false);
    
    logger.log('CDN Debug Panel available. Use: __showCDNDebug() to open');
    
    return () => {
      delete (window as any).__showCDNDebug;
      delete (window as any).__hideCDNDebug;
    };
  }, []);

  const testCDN = async () => {
    setIsTestingCDN(true);
    setTests([]);

    // Test URLs - replace with actual image URLs from your database
    const testUrls = [
      'https://dkhye8ayv4xp5.cloudfront.net/test-image.jpg',
      'https://kjss3.s3.us-east-1.amazonaws.com/test-image.jpg',
    ];

    for (const url of testUrls) {
      const startTime = performance.now();
      
      try {
        // Use img element instead of fetch to avoid CORS issues
        const loadTime = await new Promise<number>((resolve, reject) => {
          const img = new Image();
          const start = performance.now();
          
          img.onload = () => resolve(performance.now() - start);
          img.onerror = () => reject(new Error('Failed to load image'));
          
          img.src = url + '?t=' + Date.now(); // Cache bust
        });
        
        setTests(prev => [...prev, {
          url,
          status: 'success',
          loadTime: Math.round(loadTime),
          fromCache: false,
          headers: { 'note': 'Using img element (CORS-safe)' }
        }]);
        
        logger.log('CDN Test Result', { url, loadTime });
      } catch (error) {
        setTests(prev => [...prev, {
          url,
          status: 'error',
          loadTime: 0
        }]);
        
        logger.error('CDN Test Failed', { url, error });
      }
    }

    setIsTestingCDN(false);
  };

  const testActualImages = async () => {
    setIsTestingCDN(true);
    setTests([]);

    // Find all images on the page
    const images = Array.from(document.querySelectorAll('img'));
    const imageUrls = images
      .map(img => img.src)
      .filter(src => src.includes('cloudfront') || src.includes('s3'))
      .slice(0, 5); // Test first 5 images

    logger.log('Testing actual images on page', { count: imageUrls.length });

    for (const url of imageUrls) {
      const startTime = performance.now();
      
      try {
        const response = await fetch(url, { 
          method: 'HEAD',
          cache: 'reload' // Force fresh request
        });
        
        const loadTime = performance.now() - startTime;
        const headers: Record<string, string> = {};
        
        response.headers.forEach((value, key) => {
          if (['x-cache', 'age', 'content-type', 'cache-control'].includes(key.toLowerCase())) {
            headers[key] = value;
          }
        });

        setTests(prev => [...prev, {
          url: url.length > 60 ? url.substring(0, 60) + '...' : url,
          status: response.ok ? 'success' : 'error',
          loadTime: Math.round(loadTime),
          fromCache: headers['x-cache']?.includes('Hit') || false,
          headers
        }]);
      } catch (error) {
        setTests(prev => [...prev, {
          url: url.length > 60 ? url.substring(0, 60) + '...' : url,
          status: 'error',
          loadTime: 0
        }]);
      }
    }

    setIsTestingCDN(false);
  };

  const checkCDNConfig = () => {
    const cdnUrl = import.meta.env.VITE_CDN_URL;
    const hasConfig = !!cdnUrl;
    
    logger.log('CDN Configuration Check', {
      configured: hasConfig,
      url: cdnUrl || 'Not configured',
      imagesOnPage: document.querySelectorAll('img').length,
      cloudFrontImages: document.querySelectorAll('img[src*="cloudfront"]').length,
      s3Images: document.querySelectorAll('img[src*="s3"]').length
    });

    return {
      configured: hasConfig,
      url: cdnUrl,
      imageCount: document.querySelectorAll('img').length,
      cloudFrontCount: document.querySelectorAll('img[src*="cloudfront"]').length,
      s3Count: document.querySelectorAll('img[src*="s3"]').length
    };
  };

  if (!isVisible) return null;

  const config = checkCDNConfig();

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="size-5" />
            CDN Debug Panel
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsVisible(false)}
          >
            Close
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Configuration Status */}
          <div className="space-y-2">
            <h3 className="font-semibold">CDN Configuration</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                {config.configured ? (
                  <CheckCircle2 className="size-4 text-green-500" />
                ) : (
                  <XCircle className="size-4 text-red-500" />
                )}
                <span>CDN URL: {config.configured ? 'Configured' : 'Not Set'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">URL: </span>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                  {config.url || 'Not configured'}
                </code>
              </div>
              <div>
                <span className="text-muted-foreground">Images on page: </span>
                <Badge variant="secondary">{config.imageCount}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">CloudFront images: </span>
                <Badge variant={config.cloudFrontCount > 0 ? 'default' : 'secondary'}>
                  {config.cloudFrontCount}
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">S3 images: </span>
                <Badge variant={config.s3Count > 0 ? 'destructive' : 'secondary'}>
                  {config.s3Count}
                </Badge>
              </div>
            </div>
            
            {config.s3Count > 0 && config.url && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-xs font-medium text-blue-900 mb-2">
                  ℹ️ Auto-Conversion Active
                </div>
                <div className="text-xs text-blue-700">
                  S3 URLs are being automatically converted to CloudFront URLs by OptimizedImage and ImageWithFallback components.
                  <div className="mt-2 font-mono text-[10px] bg-white p-2 rounded">
                    <div className="text-red-600">Before: s3.amazonaws.com/users/...</div>
                    <div className="text-green-600 mt-1">After: {config.url}/users/...</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Test Buttons */}
          <div className="flex gap-2">
            <Button 
              onClick={testActualImages} 
              disabled={isTestingCDN}
              className="flex items-center gap-2"
            >
              {isTestingCDN ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Test Page Images
            </Button>
            <Button 
              onClick={testCDN} 
              disabled={isTestingCDN}
              variant="outline"
            >
              Test Sample URLs
            </Button>
          </div>

          {/* Test Results */}
          {tests.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Test Results</h3>
              <div className="space-y-2">
                {tests.map((test, index) => (
                  <div 
                    key={index}
                    className="border rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {test.status === 'success' ? (
                          <CheckCircle2 className="size-4 text-green-500" />
                        ) : test.status === 'error' ? (
                          <XCircle className="size-4 text-red-500" />
                        ) : (
                          <Loader2 className="size-4 animate-spin" />
                        )}
                        <code className="text-xs">{test.url}</code>
                      </div>
                      {test.loadTime && (
                        <Badge variant={test.loadTime < 200 ? 'default' : 'destructive'}>
                          {test.loadTime}ms
                        </Badge>
                      )}
                    </div>

                    {test.headers && (
                      <div className="text-xs space-y-1 pl-6">
                        {Object.entries(test.headers).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-muted-foreground">{key}:</span>
                            <code className="bg-gray-100 px-1 rounded">{value}</code>
                          </div>
                        ))}
                        {test.fromCache && (
                          <Badge variant="default" className="mt-1">
                            Served from CDN Cache! 🚀
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-sm">
            <h4 className="font-semibold">How to Verify CDN is Working:</h4>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Click "Test Page Images" to check actual images on this page</li>
              <li>Look for "x-cache: Hit from cloudfront" in headers</li>
              <li>Load times should be &lt;200ms for CDN-served images</li>
              <li>CloudFront URLs should be: dkhye8ayv4xp5.cloudfront.net</li>
              <li>Check browser DevTools → Network tab for image requests</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}